import { ImageUknown } from "@lib/constants";
import { Badge, Button, Col, Image, Row, Table } from "antd";
import Paragraph from "antd/lib/typography/Paragraph";
import moment from "moment";
import { useEffect, useState } from "react";
import { DownOutlined, RightOutlined } from '@ant-design/icons';

const DistributionType = {
    DIAMD: 'diamd',
    LUNAR: 'lunar',
}

const setDistributionUrl = (type, codes): string | undefined => {
    let url = ""

    let { diamond_no, diamond_stock_no, lunar_code } = codes;

    if (type === DistributionType.LUNAR) {
        url = `https://www.lunardistribution.com/home/search?term=${lunar_code}`
    }

    if (type === DistributionType.DIAMD) {
        url = `https://www.previewsworld.com/Catalog/${diamond_no || diamond_stock_no}`
    }

    return url;
}

const ImageBadge = ({ src = '', isVariant = null }) => {
    return (
        isVariant
            ? <Badge.Ribbon
                text={`VAR`}
                color="#4134fe"
                className="covr-variant-ribbon"
                style={{ fontVariant: 'tabular-nums' }}
            >
                <Image
                    width={77}
                    loading="lazy"
                    src={src}
                />
            </Badge.Ribbon>
            : <Image
                width={77}
                loading="lazy"
                src={src}
            />
    );
};

const getCreators = (record) => {
    const creators: any[] = [];
    if (record.penguin_cover_artist) {
        creators.push({
            name: record.penguin_cover_artist,
            type: 'Cover Artist',
        });
    }

    if (record.penguin_artist) {
        creators.push({
            name: record.penguin_artist,
            type: 'Artist',
        });
    }

    if (record.penguin_writer) {
        creators.push({
            name: record.penguin_writer,
            type: 'Writer',
        });
    }

    if (record.diamond_cover_artist && !creators.find(c => c.type === 'Cover Artist')) {
        creators.push({
            name: record.diamond_cover_artist,
            type: 'Cover Artist',
        });
    }

    if (record.diamond_artist && !creators.find(c => c.type === 'Artist')) {
        creators.push({
            name: record.diamond_artist,
            type: 'Artist',
        });
    }

    if (record.diamond_writer && !creators.find(c => c.type === 'Writer')) {
        creators.push({
            name: record.diamond_writer,
            type: 'Writer',
        });
    }

    if (record.lunar_cover_artist && !creators.find(c => c.type === 'Cover Artist')) {
        creators.push({
            name: record.lunar_cover_artist,
            type: 'Cover Artist',
        });
    }

    if (record.lunar_artist && !creators.find(c => c.type === 'Artist')) {
        creators.push({
            name: record.lunar_artist,
            type: 'Artist',
        });
    }

    if (record.lunar_writer && !creators.find(c => c.type === 'Writer')) {
        creators.push({
            name: record.lunar_writer,
            type: 'Writer',
        });
    }

    const result = creators.map(c => <li key={`${c.type} - ${c.name}`}>{c.type} - {c.name}</li>);
    return <>{result}</>;
}
const getFilteredColumns = (activeElements) => {
    let columns: any = [
        {
            title: 'status_badge',
            key: 'status_badge',
            width: 0,
            render: (record) => record?.updated || record?.new || record?.pushed ? (
                <Badge.Ribbon
                    className="status-ribbon"
                    placement={`start`}
                    text={record?.pushed ? `Pushed` : record?.updated ? `Updated` : `New`}
                    color={record?.pushed ? "#800080" : record?.updated ? "#3FA9CC" : "#457A8B"}
                />
            ) : null
        },

        Table.SELECTION_COLUMN,

        {
            title: 'Cover Art',
            key: 'cover',
            width: 77,
            hidden: !activeElements?.image,
            render: (record) => (record?.full_image
                ? <ImageBadge
                    src={record?.full_image}
                    isVariant={record?.variant_of}
                />
                : <ImageBadge
                    src={ImageUknown.COVER}
                    isVariant={record?.variant_of}
                />)
        },
        {
            title: 'Issue Details',
            key: 'issue_info_grp',
            ellipsis: false,
            render: (record) =>
                <ul className={`list`}>
                    {activeElements?.title
                        &&
                        <li>
                            <strong>Title:</strong> &nbsp;
                            {record.series_title}
                        </li>
                    }

                    {(activeElements?.issue_number && !!record.issue_number) &&
                        <li>
                            <strong>Issue:</strong> {record.issue_number}
                        </li>
                    }

                    {(activeElements?.variant_name && !!record.variant_name) &&
                        <li>
                            <strong>Variant:</strong> {record.variant_name}
                        </li>
                    }

                    {(activeElements?.variant_cover_letter && !!record.variant_cover_letter) &&
                        <li>
                            <strong>Variant Cover:</strong> {record.variant_cover_letter}
                        </li>
                    }

                    {activeElements?.cover_price_usd
                        && <li>
                            <strong>Price:</strong>
                            &nbsp;{record.cover_price_usd}
                        </li>
                    }

                    <li className={`divider`}></li>

                    {activeElements?.page_count &&
                        <li>
                            <strong>Page Count:</strong>
                            &nbsp;{record.page_count}
                        </li>
                    }

                    {(activeElements?.max_issue && !!record.max_issue) &&
                        <li>
                            <strong>Max Issue:</strong> {record.max_issue}
                        </li>
                    }

                    {(activeElements?.variant_ratio && record.variant_ratio) &&
                        <li><strong>Variant Ratio:</strong>
                            &nbsp;{record.variant_ratio}</li>
                    }

                    <li className={`divider`}></li>

                    {(activeElements?.external_link_button && (record.diamond_no || record.diamond_stock_no)) &&
                        <li className={'distribution-link'}>
                            <Button
                                target={`_blank`}
                                size={`small`}
                                type={`link`}
                                href={setDistributionUrl(DistributionType.DIAMD, {
                                    diamond_no: record.diamond_no,
                                    diamond_stock_no: record.diamond_stock_no,
                                })}>
                                Previews World
                            </Button>
                        </li>}

                    {(activeElements?.external_link_button && record.lunar_code) &&
                        <li className={'distribution-link'}>
                            <Button
                                target={`_blank`}
                                size={`small`}
                                type={`link`}
                                href={setDistributionUrl(DistributionType.LUNAR, {
                                    lunar_code: record.lunar_code,
                                })}>
                                Lunar Distribution
                            </Button>
                        </li>}
                </ul>
        },
        {
            title: 'Creators',
            key: 'creators_grp',
            ellipsis: false,
            hidden: !activeElements?.creators,
            render: (record) =>
                <ul
                    className={`list`}
                >
                    <li><strong>Creator(<span className="lowercase">s</span>):</strong></li>
                    {getCreators(record)}
                </ul>
        },
        {
            title: 'Identifier',
            key: 'other_grp',
            render: (record) => <ul
                className={`list`}
            >
                {(activeElements?.isbn_and_barcode && record?.isbn) &&
                    <li><strong>ISBN:</strong>
                        &nbsp;{record?.isbn}</li>
                }

                {(activeElements?.isbn_and_barcode && record?.barcode) &&
                    <li>
                        <strong>UPC:</strong>
                        &nbsp;{record?.barcode}
                    </li>
                }

                <li className={`divider`}></li>

                {(activeElements?.sku_data && record?.diamond_stock_no) &&
                    <li>
                        <strong>Diamond Stock No:</strong>
                        &nbsp;{record?.diamond_stock_no}
                    </li>
                }

                {(activeElements?.sku_data && record?.diamond_no) &&
                    <li>
                        <strong>Diamond No:</strong>
                        &nbsp;{record?.diamond_no}
                    </li>
                }

                {(activeElements?.sku_data && record?.lunar_code) &&
                    <li>
                        <strong>Lunar Code:</strong>
                        &nbsp;{record.lunar_code}
                    </li>
                }

                <li className={`divider`}></li>

                {activeElements?.foc_date &&
                    <li>
                        <strong>FOC:</strong>
                        {record.pushed ? <>&nbsp;<s>{moment(record.downloaded_foc_date).format('YYYY-MM-DD')}</s></> : null}&nbsp;{moment(record.foc_date).format('YYYY-MM-DD')}
                    </li>
                }

                {activeElements?.post_date &&
                    <li>
                        <strong>Release:</strong>
                        &nbsp;{moment(record.post_date).format('YYYY-MM-DD')}
                    </li>
                }
            </ul>
        },
        {
            title: 'Summary',
            key: 'summary_grp',
            ellipsis: false,
            width: `30%`,
            hidden: !activeElements?.summary,
            render: (record) => <>
                <strong>Summary:</strong>
                <br />
                <Paragraph
                    ellipsis={{
                        rows: 4,
                        expandable: true,
                        symbol: 'more'
                    }}>
                    {record.summary}
                </Paragraph>
            </>
        },
    ];

    return columns.filter(item => !item.hidden);
}

const columns = [
    {
        title: 'Series Name',
        key: 'title',
        ellipsis: false,
        defaultExpandAllRows: true,
        render: (record) => record?.title
            ? <span className="title">{record.title} ({record.items.length})</span>
            : null
    },
    Table.EXPAND_COLUMN,
];

const IssueDataTable = ({
    dataSource = [],
    isLoading = false,
    expandedRowIds = [],
    selectedRowIds = [],
    deselectedRowIds = [],
    onSelectChange,
    activeElements,
}) => {

    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    const [deselectRowKeys, setDeselectRowKeys] = useState<React.Key[]>([]);

    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>(expandedRowIds);

    const childColumns = getFilteredColumns(activeElements);

    useEffect(() => {
        if (selectedRowIds.length > 0) {
            setSelectedRowKeys(selectedRowIds);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRowIds]);

    useEffect(() => {

        if (deselectedRowIds.length > 0) {
            setDeselectRowKeys(deselectedRowIds);
        }

    }, [deselectedRowIds]);

    useEffect(() => {

        if (expandedRowIds.length > 0) {
            setExpandedRowKeys(expandedRowIds);
        }

    }, [expandedRowIds]);


    const handleSelectChange = (issue, selected) => {

        const selectedIds = selected
            ? [...selectedRowKeys, issue.post_id]
            : selectedRowKeys
                .filter((v) => v !== issue.post_id);

        const deselectedIds = !selected
            ? [...deselectRowKeys, issue.post_id]
            : deselectRowKeys
                .filter((v) => v !== issue.post_id);

        setSelectedRowKeys(selectedIds);

        setDeselectRowKeys(deselectedIds);

        onSelectChange({
            issue, selected, ids: {
                selected: selectedIds,
                deselected: deselectedIds,
            }
        });
    };

    const handleExpandRowChange = (expanded, selected) => {

        const expandIds = expanded
            ? [...expandedRowKeys, selected.key]
            : expandedRowKeys
                .filter((v) => v !== selected.key);

        setExpandedRowKeys(expandIds);
    };

    return (
        <Row
            className={`pp-issue-data-table`}
            justify={`space-between`}
        >
            <Col span={24}>
                <Table
                    rowKey={`key`}
                    columns={columns}
                    showHeader={false}
                    pagination={false}
                    loading={isLoading}
                    dataSource={dataSource}
                    rowClassName={`parent-expand-row`}
                    expandable={{
                        expandRowByClick: true,
                        expandIcon: ({ expanded, onExpand, record }) =>
                            expanded ? (
                                <DownOutlined
                                    style={{
                                        marginTop: `3px`,
                                        fontSize: `16px`
                                    }}
                                    onClick={e => onExpand(record, e)}
                                />
                            ) : (
                                <RightOutlined style={{
                                    marginTop: `3px`,
                                    fontSize: `16px`
                                }}
                                    onClick={e => onExpand(record, e)}
                                />
                            ),
                        onExpand: handleExpandRowChange,
                        expandedRowKeys: expandedRowKeys,
                        expandedRowRender: (record: any) =>
                            <Table
                                rowKey={`post_id`}
                                rowClassName={`child-expand-row`}
                                className={`expand-children`}
                                columns={childColumns}
                                dataSource={record.items}
                                pagination={false}
                                showHeader={false}
                                rowSelection={{
                                    selectedRowKeys,
                                    onSelect: handleSelectChange,
                                }}
                            />
                    }}
                />

            </Col>
        </Row>
    );
};

export default IssueDataTable;


